import React, { useState, useEffect } from "react";
import ReactTable from "react-table-v6";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatDate } from "../../../../../../formatDate";
import commaNumber from "comma-number";
import Loader from "../../../../../../settings/loader";
import "react-table-v6/react-table.css";
import "../style.scss";

function Slide3(props) {
  const [data, setData] = useState({
    userStats: [],
  });
  const [load, setLoad] = useState(0);

  const COLORS = {
    web: "#0088FE",
    android: "#00C49F",
    ios: "#FFBB28",
  };

  useEffect(() => {
    if (props.data.state && !load) {
      const loginDetails = props.data.result.loginDetails;
      const userMap = props.data.result.userMap;

      // Group by user to get per-user statistics
      const userStatsMap = {};
      loginDetails.forEach((login) => {
        const userId = login.userId;

        // Get user details from userMap
        const userDetails = userMap[userId] || {
          name: "Unknown User",
          email: "N/A",
        };

        if (!userStatsMap[userId]) {
          userStatsMap[userId] = {
            userId: userId,
            userName: userDetails.name,
            email: userDetails.email,
            web: { count: 0, lastLogin: null },
            android: { count: 0, lastLogin: null },
            ios: { count: 0, lastLogin: null },
            totalLogins: 0,
          };
        }

        const platform = login.platform;
        const loginTime = login.loginTime; // Convert to milliseconds

        if (
          platform === "web" ||
          platform === "android" ||
          platform === "ios"
        ) {
          userStatsMap[userId][platform].count++;
          if (
            !userStatsMap[userId][platform].lastLogin ||
            loginTime > userStatsMap[userId][platform].lastLogin
          ) {
            userStatsMap[userId][platform].lastLogin = loginTime;
          }
        }
        userStatsMap[userId].totalLogins++;
      });

      const userStats = Object.values(userStatsMap);

      setData({
        userStats: userStats,
      });
      setLoad(1);
    }
  }, [props.data, load]);

  // Export logic
  useEffect(() => {
    if (props.export && data.userStats.length) {
      props.getData({
        type: "table",
        title: "Device Usage Statistics",
        body: [
          {
            head: [
              ["User Name", "Email", "Web", "Android", "iOS", "Total Logins"],
            ],
            body: data.userStats.map((user) => [
              user.userName || "Unknown",
              user.email || "N/A",
              user.web?.count || 0,
              user.android?.count || 0,
              user.ios?.count || 0,
              user.totalLogins,
            ]),
          },
        ],
      });
    }
    // eslint-disable-next-line
  }, [props.export, data.userStats]);

  const columns = [
    {
      Header: "User Name",
      accessor: "userName",
      Cell: (row) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FontAwesomeIcon
            icon={["fas", "user"]}
            style={{ marginRight: "8px", color: "#666" }}
          />
          {row.value || "Unknown User"}
        </div>
      ),
    },
    {
      Header: "Email",
      accessor: "email",
      Cell: (row) => row.value || "N/A",
    },
    {
      Header: () => (
        <div>
          <FontAwesomeIcon
            icon={["fas", "desktop"]}
            style={{ marginRight: "5px" }}
          />
          Web
        </div>
      ),
      accessor: "web",
      Cell: (row) => {
        const count = row.value?.count || 0;
        const lastLogin = row.value?.lastLogin;
        return (
          <div>
            <div style={{ fontWeight: "bold", color: "#212121ff" }}>
              {commaNumber(count)}
            </div>
            {lastLogin && (
              <div style={{ fontSize: "11px", color: "#999" }}>
                Last: {formatDate.longShort(lastLogin / 1000)}
              </div>
            )}
          </div>
        );
      },
      sortMethod: (a, b) => {
        const countA = a?.count || 0;
        const countB = b?.count || 0;
        return countA - countB;
      },
    },
    {
      Header: () => (
        <div>
          <FontAwesomeIcon
            icon={["fas", "mobile-alt"]}
            style={{ marginRight: "5px" }}
          />
          Android
        </div>
      ),
      accessor: "android",
      Cell: (row) => {
        const count = row.value?.count || 0;
        const lastLogin = row.value?.lastLogin;
        return (
          <div>
            <div style={{ fontWeight: "bold", color: "#212121ff" }}>
              {commaNumber(count)}
            </div>
            {lastLogin && (
              <div style={{ fontSize: "11px", color: "#999" }}>
                Last: {formatDate.longShort(lastLogin / 1000)}
              </div>
            )}
          </div>
        );
      },
      sortMethod: (a, b) => {
        const countA = a?.count || 0;
        const countB = b?.count || 0;
        return countA - countB;
      },
    },
    {
      Header: () => (
        <div>
          <FontAwesomeIcon
            icon={["fab", "apple"]}
            style={{ marginRight: "5px" }}
          />
          iOS
        </div>
      ),
      accessor: "ios",
      Cell: (row) => {
        const count = row.value?.count || 0;
        const lastLogin = row.value?.lastLogin;
        return (
          <div>
            <div style={{ fontWeight: "bold", color: "#212121ff" }}>
              {commaNumber(count)}
            </div>
            {lastLogin && (
              <div style={{ fontSize: "11px", color: "#999" }}>
                Last: {formatDate.longShort(lastLogin / 1000)}
              </div>
            )}
          </div>
        );
      },
      sortMethod: (a, b) => {
        const countA = a?.count || 0;
        const countB = b?.count || 0;
        return countA - countB;
      },
    },
    {
      Header: "Total Logins",
      accessor: "totalLogins",
      Cell: (row) => (
        <div style={{ fontWeight: "bold", fontSize: "14px" }}>
          {commaNumber(row.value)}
        </div>
      ),
    },
  ];

  return (
    <div className="device-analytics-slide slide3">
      {!load ? (
        <Loader />
      ) : (
        <div className="table-container">
          <ReactTable
            className="-striped -highlight device-stats-table"
            data={data.userStats}
            columns={columns}
            defaultPageSize={10}
            sortable
            filterable
            defaultSorted={[
              {
                id: "totalLogins",
                desc: true,
              },
            ]}
            getTdProps={() => ({
              style: {
                textAlign: "center",
                padding: "12px",
              },
            })}
          />
        </div>
      )}
    </div>
  );
}

export default Slide3;
